import { BehaviorSubject, fromEvent } from 'rxjs'
import { debounceTime, map, scan } from 'rxjs/operators'

function _getZeroBounds () {
  return {
    center: { x: 0, y: 0 },
    max: { x: 0, y: 0 },
    min: { x: 0, y: 0 }
  }
}

const _tempPanAreaSize = {}

function randomOffset (min, max, direction) {
  const offset = (min + Math.random() * (max - min)) * direction
  return Math.round(offset * 1000) / 1000
}

// function _calculateItemSize (item, viewportSize, zoomLevel) {
//   if (item.src && !item.loadError) {
//     _tempPanAreaSize.x = viewportSize.x
//     _tempPanAreaSize.y = viewportSize.y - 88

//     var hRatio = _tempPanAreaSize.x / item.w
//     var vRatio = _tempPanAreaSize.y / item.h

//     item.fitRatio = hRatio < vRatio ? hRatio : vRatio
//   } else {
//     item.w = item.h = 0
//   }
// }

function _calculateSingleItemPanBounds (item, realPanElementW, realPanElementH) {
  var bounds = item.bounds

  // position of element when it's centered
  bounds.center.x = Math.round((_tempPanAreaSize.x - realPanElementW) / 2)
  bounds.center.y = Math.round((_tempPanAreaSize.y - realPanElementH) / 2) + item.vGap.top

  // maximum pan position
  bounds.max.x = (realPanElementW > _tempPanAreaSize.x)
    ? Math.round(_tempPanAreaSize.x - realPanElementW)
    : bounds.center.x

  bounds.max.y = (realPanElementH > _tempPanAreaSize.y)
    ? Math.round(_tempPanAreaSize.y - realPanElementH) + item.vGap.top
    : bounds.center.y

  // minimum pan position
  bounds.min.x = (realPanElementW > _tempPanAreaSize.x) ? 0 : bounds.center.x
  bounds.min.y = (realPanElementH > _tempPanAreaSize.y) ? item.vGap.top : bounds.center.y
}

function _calculateItemSize (item, viewportSize) {
  if (item.src && !item.loadError) {
    if (!item.vGap) {
      item.vGap = { top: 100, bottom: 100 }
    }
    // allows overriding vertical margin for individual items
    // _shout('parseVerticalMargin', item)

    _tempPanAreaSize.x = viewportSize.x
    _tempPanAreaSize.y = viewportSize.y - item.vGap.top - item.vGap.bottom

    var hRatio = _tempPanAreaSize.x / item.w
    var vRatio = _tempPanAreaSize.y / item.h

    item.fitRatio = hRatio < vRatio ? hRatio : vRatio
    // item.fillRatio = hRatio > vRatio ? hRatio : vRatio;

    const zoomLevel = 1

    item.initialZoomLevel = zoomLevel

    if (!item.bounds) {
      // reuse bounds object
      item.bounds = _getZeroBounds()
    }

    _calculateSingleItemPanBounds(item, item.w * zoomLevel, item.h * zoomLevel)

    if (zoomLevel === item.initialZoomLevel) {
      item.initialPosition = item.bounds.center
    }

    return item.bounds
  } else {
    item.w = item.h = 0
    item.initialZoomLevel = item.fitRatio = 1
    item.bounds = _getZeroBounds()
    item.initialPosition = item.bounds.center

    // if it's not image, we return zero bounds (content is not zoomable)
    return item.bounds
  }
}

const QUEUE_MAX_LENGTH = 6
export default class PhotoSwipeBackdropController {
  constructor (photoSwipe, photoSwipeController) {
    this._photoSwipe = photoSwipe
    this._photoSwipeController = photoSwipeController
    this._offsetDirectionCounter = 0

    this._initBackdrop()
  }

  notifyChange () {
    this._latestIndex.next(this._photoSwipe.getCurrentIndex())
  }

  destroy () {
    if (this._resizeSubscription) {
      this._resizeSubscription.unsubscribe()
    }
  }

  _initBackdrop () {
    this._clearBackdrop()

    this._latestIndex = new BehaviorSubject(this._photoSwipe.getCurrentIndex())

    const latestItem = this._latestIndex.pipe(map((index) => {
      return this._photoSwipe.items[index]
    }))

    const latestItemElement = latestItem.pipe(map((item) => {
      return this._getItemElement(item)
    }))

    latestItemElement.pipe(
      scan((acc, val) => {
        const existingIndex = acc.indexOf(val)
        if (existingIndex >= 0) {
          acc.splice(existingIndex, 1)
        }
        acc.unshift(val)
        return acc.slice(0, QUEUE_MAX_LENGTH + 2)
      }, [])
    ).subscribe((arr) => {
      for (let i = 0; i < arr.length; i++) {
        if (i === 0) {
          arr[i].style.display = 'none'
        } else {
          arr[i].style.display = 'block'
          arr[i].style.zIndex = arr.length - (i - 1)
        }
      }
    }
    )

    this._resizeSubscription =
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(200)
        ).subscribe(() => {
          this._updateAllItemElements()
        })

    this._feedInitialIndices()
  }

  _feedInitialIndices () {
    const initialBackdropIndices = this._getInitialBackdropIndices()
    initialBackdropIndices.reverse().forEach(index => {
      this._latestIndex.next(index)
    })
  }

  _getItemElement (item) {
    let itemElement = this._findItemElement(item)
    if (!itemElement) {
      itemElement = this._createItemElement(item)
    }
    return itemElement
  }

  _findItemElement (item) {
    const bgElement = this._photoSwipe.bg
    return bgElement.querySelector('[data-pwsp-backdrop-item="' + item.pid + '"]')
  }

  _getNextOffset () {
    const xDirection = (3 - this._offsetDirectionCounter) % 3 ? 1 : -1
    const yDirection = (3 - this._offsetDirectionCounter) % 2 ? 1 : -1
    this._offsetDirectionCounter = (this._offsetDirectionCounter + 1) % 4

    const minShiftY = 1
    const maxShiftY = 2
    const minShiftX = 1
    const maxShiftX = 4
    const shiftX = randomOffset(minShiftX, maxShiftX, xDirection)
    const shiftY = randomOffset(minShiftY, maxShiftY, yDirection)
    return [shiftX, shiftY]
  }

  _createItemElement (item) {
    const bgElement = this._photoSwipe.bg
    this._photoSwipeController._setImageProperties(item)
    _calculateItemSize(item, this._photoSwipe.viewportSize, 0)

    const src = item.src
    const imgElement = document.createElement('img')
    imgElement.setAttribute('alt', '')
    imgElement.setAttribute('src', src)

    const layerElement = document.createElement('div')
    layerElement.setAttribute('class', 'pswp__bg__layer')
    layerElement.setAttribute('data-pwsp-backdrop-item', item.pid)

    const [shiftX, shiftY] = this._getNextOffset()

    const transform = `translate(${shiftX}rem, ${shiftY}rem)`
    layerElement.style.transform = transform
    layerElement.appendChild(imgElement)

    bgElement.appendChild(layerElement)

    this._setImageSize(item, imgElement, false)
    this._setLayerScale(layerElement, item)

    return layerElement
  }

  _setLayerScale (itemElement, item) {
    const scale = item.fitRatio > 1 ? Math.round(1 / item.fitRatio * 10000) / 10000 : 1
    itemElement.style.transform = itemElement.style.transform.replace(/scale([^)]*)/g, '').trim() + ` scale(${scale})`
  }

  _updateAllItemElements () {
    this._photoSwipe.items
      .map(item => {
        return [this._findItemElement(item), item]
      })
      .filter(([itemElement, item]) => itemElement)
      .forEach(([itemElement, item]) => {
        this._updateItemElement(itemElement, item)
      })
  }

  _updateItemElement (itemElement, item) {
    _calculateItemSize(item, this._photoSwipe.viewportSize, 0)

    this._setLayerScale(itemElement, item)

    const imgElement = itemElement.children[0]
    this._setImageSize(item, imgElement, false)
  }

  _clearBackdrop () {
    const bgElement = this._photoSwipe.bg
    bgElement.innerHTML = ''
  }

  _getInitialBackdropIndices () {
    const currentIndex = this._photoSwipe.getCurrentIndex()
    const items = this._photoSwipe.items

    const totalItems = items.length
    const startIndex = currentIndex - QUEUE_MAX_LENGTH

    const indicesSet = new Set()
    for (let index = currentIndex; index >= startIndex; index--) {
      const actualIndex = (totalItems + (index % totalItems)) % totalItems
      indicesSet.add(actualIndex)
    }

    return Array.from(indicesSet)
  }

  _setImageSize (item, img, maxRes) {
    if (!item.src) {
      return
    }

    if (!img) {
      img = item.container.lastChild
    }

    var w = maxRes ? item.w : Math.round(item.w * item.fitRatio)
    var h = maxRes ? item.h : Math.round(item.h * item.fitRatio)

    if (item.placeholder && !item.loaded) {
      item.placeholder.style.width = w + 'px'
      item.placeholder.style.height = h + 'px'
    }

    img.style.width = w + 'px'
    img.style.height = h + 'px'
  }
}
